/**
 * Bildirim merkezi durumu. Kaynak: `Tb_Rn_Sys_Notifications` (SP'den `Sp_Rn_Sys_Notify` ile
 * ya da sunucu kodundan yazılır); DAL dağıtıcısı her yeni kaydı `NotificationReceived` olarak
 * anlık iletir. Liste ilk açılışta çekilir, sonrası olaylarla güncellenir.
 */
import { create } from 'zustand';
import { usePrefsStore } from '@/entities/preferences/model/prefsStore';
import { playOctoSound } from '@/shared/lib/sound/octoSounds';
import { notificationsApi } from '../api/notificationsApi';
import type { AppNotification } from './types';

interface NotificationState {
  open: boolean;
  items: AppNotification[];
  unread: number;
  hasMore: boolean;
  loaded: boolean;
  loading: boolean;
  setOpen: (open: boolean) => void;
  load: (more?: boolean) => Promise<void>;
  refreshCount: () => Promise<void>;
  apply: (n: AppNotification) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  remove: (id: string) => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  open: false,
  items: [],
  unread: 0,
  hasMore: false,
  loaded: false,
  loading: false,

  setOpen: (open) => {
    set({ open });
    if (open) void get().load();
  },

  load: async (more = false) => {
    if (get().loading) return;
    set({ loading: true });
    try {
      const before = more ? get().items[get().items.length - 1]?.addDate : undefined;
      const res = await notificationsApi.list(before);
      set((s) => {
        const items = more ? [...s.items, ...res.items.filter((n) => !s.items.some((x) => x.notificationId === n.notificationId))] : res.items;
        return { items, unread: res.unread, hasMore: res.hasMore, loaded: true, loading: false };
      });
    } catch {
      set({ loading: false, loaded: true });
    }
  },

  refreshCount: async () => {
    try {
      set({ unread: (await notificationsApi.unreadCount()).count });
    } catch {
      /* noop */
    }
  },

  apply: (n) => {
    if (get().items.some((x) => x.notificationId === n.notificationId)) return;
    set((s) => ({ items: s.loaded ? [n, ...s.items] : s.items, unread: s.unread + (n.isRead ? 0 : 1) }));
    const prefs = usePrefsStore.getState().prefs;
    playOctoSound(prefs.soundNotification, prefs.soundVolume);
  },

  markRead: (id) => {
    const n = get().items.find((x) => x.notificationId === id);
    if (n?.isRead) return;
    set((s) => ({
      items: s.items.map((x) => (x.notificationId === id ? { ...x, isRead: true } : x)),
      unread: Math.max(0, s.unread - 1),
    }));
    void notificationsApi.read(id);
  },

  markAllRead: () => {
    set((s) => ({ items: s.items.map((x) => ({ ...x, isRead: true })), unread: 0 }));
    void notificationsApi.readAll();
  },

  remove: (id) => {
    const n = get().items.find((x) => x.notificationId === id);
    set((s) => ({
      items: s.items.filter((x) => x.notificationId !== id),
      unread: n && !n.isRead ? Math.max(0, s.unread - 1) : s.unread,
    }));
    void notificationsApi.remove(id);
  },
}));
